"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var angular_1 = require("angular");
var lodash_1 = __importDefault(require("lodash"));
var PAGE_BREAK_WIDGET_TYPE = 'pr.pagebreak';
var SERVICE_NAME = 'prSectionManager';
(function (module) {
    'use strict';
    module.factory(SERVICE_NAME, [
        '$q',
        'prActiveReport',
        'rsEnvironment',
        'rsWidgets',
        'rsState',
        'pr.widgetManager',
        'prHiddenRenderer',
        'prWidgetChunkerFactory',
        'prAutoHeightHelper',
        'pr.navigationService',
        'prConditionalDisplay',
        'prSectionManagerHelper',
        'pr.loggerService',
        function ($q, report, env, rsWidgets, rsState, widgetManager, hiddenRenderer, chunker, autoHeightHelper, navigationService, conditionalDisplay, sectionManagerHelper, loggerService) {
            var containerId = report.getReportId();
            var loggerManager = loggerService.getLoggerManager(SERVICE_NAME);
            /**
             * Get All Widgets
             * For new layout engine:
             *  - Loop through all container pages and get widgets of each page
             * For old layout engine:
             *  - Get widgets of the first container page
             *
             * @return {array} widgets
             */
            function getWidgets() {
                var sections = sectionManagerHelper.getSectionsToLoad(report.getPages());
                var widgets = getWidgetsFromSections(sections);
                // show all widgets in editor
                if (isEditorWebView()) {
                    return widgets;
                }
                return getWidgetsInPublicView(widgets);
            }
            function getWidgetsFromSections(sections) {
                var widgets = [];
                lodash_1.default.forEach(sections, function (section) {
                    var widgetsOfSection = (section || {}).widgets || [];
                    lodash_1.default.forEach(widgetsOfSection, function (widget) {
                        if (!widget.sectionId) {
                            widget.sectionId = section._id;
                        }
                        widgets.push(widget);
                    });
                });
                return widgets;
            }
            /**
             * get widgets in public view
             *
             * @param  {array} all widgets
             * @return {array} widgets that apply conditional display logic
             */
            function getWidgetsInPublicView(widgets) {
                var widgetsInPublic = [];
                lodash_1.default.forEach(widgets, function (widget) {
                    if (conditionalDisplay.isHiddenInPublic(widget._id)) {
                        return;
                    }
                    if (isPageBreak(widgetsInPublic[widgetsInPublic.length - 1]) && isPageBreak(widget)) {
                        // replace the empty section's pr.pagebreak with the newer one so non-empty
                        // pages contain a pr.pagebreak associated with the corresponding sectionId
                        widgetsInPublic[widgetsInPublic.length - 1] = widget;
                        return;
                    }
                    widgetsInPublic.push(widget);
                });
                if (isPageBreak(widgetsInPublic[widgetsInPublic.length - 1])) {
                    widgetsInPublic.pop();
                }
                return widgetsInPublic;
            }
            /**
             * save after a widget has been added
             *
             * @param {object} addedWidget
             * @param {array} allWidgets, which already contains the added widget
             */
            function addWidget(addedWidget, allWidgets) {
                if (!isSaveAllowed()) {
                    return $q.resolve();
                }
                if (isPageBreak(addedWidget)) {
                    return addPageBreak(addedWidget, allWidgets);
                }
                return saveWidget(addedWidget, allWidgets);
            }
            /**
             * Save a non-pagebreak widget to the section it belongs to
             *
             * @param  {object} widget
             * @param  {array} allWidgets, which already contains the added widget
             */
            function saveWidget(widget, allWidgets) {
                if (!isSaveAllowed()) {
                    return $q.resolve();
                }
                allWidgets = allWidgets || getWidgets();
                var sectionId = report.getPageIdOfWidget(widget);
                var widgetsToSave = sectionManagerHelper.getWidgetsToSave(widget, allWidgets);
                return updateSectionWithWidgets(sectionId, widgetsToSave);
            }
            /**
             * Update a section with an array of widgets
             *
             * @param  {string} sectionId
             * @param  {array} widgets
             * @return {promise}
             */
            function updateSectionWithWidgets(sectionId, widgets) {
                var page = report.getPage(sectionId);
                page.widgets = widgets;
                return report.setPage(page);
            }
            /**
             * Add a page break and save
             * - update the original section (remove widgets that are moved)
             * - add a new section with widgets that should be moved
             * - update the widget state page with new section Id
             *
             * @param {object} pagebreak
             * @param {array} allWidgets, which already contains the added page break
             */
            function addPageBreak(pagebreak, allWidgets) {
                allWidgets = allWidgets || getWidgets();
                var currIndex = lodash_1.default.findIndex(allWidgets, { _id: pagebreak._id });
                var indexOfPrevPb = sectionManagerHelper.getIndexOfPrevPb(currIndex, allWidgets);
                var widgetsOfPrevSection = allWidgets.slice(indexOfPrevPb, currIndex);
                var prevSectionId = report.getPageIdOfWidget(widgetsOfPrevSection[0]);
                var indexOfNextPb = sectionManagerHelper.getIndexOfNextPb(currIndex, allWidgets);
                var widgetsOfNewSection = allWidgets.slice(currIndex, indexOfNextPb);
                // update prev section (do this first so the ui is responsive)
                var oldSectionP = updateSectionWithWidgets(prevSectionId, widgetsOfPrevSection);
                // insert a new section after the prevSection (run immediately so that the widgets array is updated)
                var prevSectionIndex = report.getPageIndex(prevSectionId);
                var newSectionP = report.addPage({
                    widgets: widgetsOfNewSection
                }, prevSectionIndex + 1);
                return oldSectionP.then(function () { return newSectionP; }).then(function (newSection) {
                    // update widget state page of widgets in new section
                    return updateStatePageOfWidgets(widgetsOfNewSection, prevSectionId, newSection._id);
                });
            }
            /**
             * save the section where the widget is deleted from
             *
             * @param  {object} deletedWidget
             * @param  {array} allWidgets, which already removes the deleted widget
             */
            function deleteWidget(deletedWidget, allWidgets) {
                if (!isSaveAllowed()) {
                    return $q.resolve();
                }
                if (isPageBreak(deletedWidget)) {
                    return deletePageBreak(deletedWidget, allWidgets);
                }
                else {
                    // save the other widgets in the section
                    var sectionIdOfDeletedWidget = report.getPageIdOfWidget(deletedWidget);
                    var widgetsToSave = [];
                    for (var i = 0; i < allWidgets.length; i++) { // tslint:disable-line
                        var widget = allWidgets[i];
                        var sectionIdOfWidget = report.getPageIdOfWidget(widget);
                        if (sectionIdOfWidget === sectionIdOfDeletedWidget) {
                            widgetsToSave.push(widget);
                        }
                    }
                    return updateSectionWithWidgets(sectionIdOfDeletedWidget, widgetsToSave);
                }
            }
            /**
             * delete a page break
             * - merge widgets of two sections and save in the first section
             * - update widget state page of widgets that are originally in the second section
             * - delete the second section
             *
             * @param  {object} deletedPageBreak
             * @param  {array} allWidgets, which already removes deletedPageBreak
             */
            function deletePageBreak(deletedPageBreak, allWidgets) {
                var widgets = allWidgets || getWidgets();
                var targetWidget;
                var targetIndex;
                for (var i = 0; i < widgets.length; i++) {
                    if (widgets[i].sectionId === deletedPageBreak.sectionId) {
                        targetWidget = widgets[i];
                        targetIndex = i;
                        break;
                    }
                }
                if (!targetWidget) {
                    // if no widgets were in the same section with the deletedPageBreak
                    // just remove the section
                    return report.removePage({ _id: deletedPageBreak.sectionId });
                }
                // if there are widgets that were in the same section with the deletedPageBreak
                // find widgets of the section before the deleted page break
                var indexOfPrevPb = sectionManagerHelper.getIndexOfPrevPb(targetIndex, widgets);
                var widgetsBefore = widgets.slice(indexOfPrevPb, targetIndex);
                var newSectionId = report.getPageIdOfWidget(widgetsBefore[0]);
                // find widgets of the section after the deleted page break
                var indexOfNextPb = sectionManagerHelper.getIndexOfNextPb(targetIndex, widgets);
                var widgetsAfter = widgets.slice(targetIndex, indexOfNextPb);
                var oldSectionId = report.getPageIdOfWidget(widgetsAfter[0]);
                // update prev section
                return updateSectionWithWidgets(newSectionId, widgets.slice(indexOfPrevPb, indexOfNextPb)).then(function () {
                    // update widget state page
                    return updateStatePageOfWidgets(widgetsAfter, oldSectionId, newSectionId);
                }).then(function () {
                    // only delete the old page if all widgets successfully prepped for new page
                    report.removePage({ _id: oldSectionId });
                });
            }
            /**
             * Loop through an array of widgets,
             * call report.updateWidgetSection to update the pageId of each widget state
             *
             * @param  {array} widgets
             * @param  {string} oldSectionId
             * @param  {string} newSectionId
             */
            function updateStatePageOfWidgets(widgets, oldSectionId, newSectionId) {
                var promises = [];
                var updatedWidgets = [];
                var errorThrown;
                var _loop_1 = function (i) {
                    var widget = widgets[i];
                    widget.sectionId = newSectionId;
                    // skip page breaks
                    if (isPageBreak(widget)) {
                        return "continue";
                    }
                    // unload the widget from the old section, so ripsaw client can remove the old cache
                    rsWidgets.unload(widget._id, oldSectionId);
                    if (autoHeightHelper.isTable(widget._type)) {
                        hiddenRenderer.removeWidget(widget); // remove from cache
                        chunker.removeChunker(widget); // remove from cache
                    }
                    var promise = rsState.updateWidgetStatePage(widget._id, oldSectionId, newSectionId, containerId)
                        .then(function () {
                        updatedWidgets.push(widget._id);
                    }).catch(function (error) {
                        logError('updateWidgetStatePage error', widget._id, error);
                        errorThrown = error;
                    });
                    promises.push(promise);
                };
                for (var i = 0; i < widgets.length; i++) {
                    _loop_1(i);
                }
                return $q.all(promises).then(function () {
                    return errorThrown ? onFailure() : onSuccess();
                });
                function onSuccess() {
                    for (var i = 0; i < widgets.length; i++) { // tslint:disable-line
                        var widget = widgets[i];
                        if (isPageBreak(widget)) {
                            continue;
                        }
                        // notify widget to make changes in UI
                        // this call would trigger a rsState.saveState call,
                        // which must be done after rsState.updateWidgetStatePage is done
                        widgetManager.notifyWidget(widget._id, 'widgetSectionUpdated', 'prSectionManager');
                    }
                }
                /**
                 * If any of the calls to updateWidgetStatePage errors, then we need to undo all changes.
                 * This involves undoing changes to widget states and the container definition.
                 * (widget states and container state are handled in separate databases)
                 *
                 * Undo widget state changes by calling updateWidgetStatePage on each widget that was updated.
                 * Throw an error so that a calling function can undo changes to the container state.
                 */
                function onFailure() {
                    return $q.all(updatedWidgets.map(function (widgetId) {
                        return rsState.updateWidgetStatePage(widgetId, newSectionId, oldSectionId, containerId).catch(function (error) {
                            logError('error while trying to undo updateWidgetStatePage', widgetId, error);
                        });
                    })).then(function () {
                        throw errorThrown;
                    });
                }
                function logError(message, widgetId, error) {
                    loggerManager.error({
                        message: message,
                        widgetId: widgetId,
                        oldSectionId: oldSectionId,
                        newSectionId: newSectionId,
                        error: error
                    });
                }
            }
            /**
             * Save after transform - resize or move
             *
             * @param  {array} moved or resized widgets
             * Note: moved or resized widgets are always in the same section.
             */
            function finishTransform(widgets, allWidgets) {
                if (!isSaveAllowed()) {
                    return;
                }
                if (!(widgets && widgets.length)) {
                    return;
                }
                allWidgets = allWidgets || getWidgets();
                var srcSectionId = report.getPageIdOfWidget(widgets[0]);
                var widgetsToSaveInDestSection = sectionManagerHelper.getWidgetsToSave(widgets[0], allWidgets);
                var destSectionId = report.getPageIdOfWidget(widgetsToSaveInDestSection[0]);
                if (!isPageBreak(widgetsToSaveInDestSection[0])) {
                    // only the first section doesn't start with a page break
                    destSectionId = env.get('pageId');
                }
                // update destSection
                return updateSectionWithWidgets(destSectionId, widgetsToSaveInDestSection).then(function () {
                    if (srcSectionId === destSectionId) {
                        return;
                    }
                    // if widgets have been moved to another section
                    // update widget state page
                    return updateStatePageOfWidgets(widgets, srcSectionId, destSectionId).then(function () {
                        // update srcSection
                        var widgetIds = lodash_1.default.map(widgets, '_id');
                        var firstWidgetInSrcSection = lodash_1.default.find(allWidgets, function (widget) {
                            /**
                             * some of the widgets that are moved from the src to dest.
                             * may still have the the src sectionId on the .sectionId property.
                             * make sure we don't count these widgets as the first widget in the src section
                             */
                            return widget.sectionId === srcSectionId && !lodash_1.default.includes(widgetIds, widget._id);
                        });
                        var widgetsToSaveInSrcSection = [];
                        if (firstWidgetInSrcSection) {
                            widgetsToSaveInSrcSection = sectionManagerHelper.getWidgetsToSave(firstWidgetInSrcSection, allWidgets);
                        }
                        return updateSectionWithWidgets(srcSectionId, widgetsToSaveInSrcSection);
                    });
                });
            }
            /**
             * Save is not allowed when:
             * - the report is not in editor view (preview or public report)
             * - widget motion hasn't stopped
             *
             * @return {Boolean}
             */
            function isSaveAllowed() {
                if (!isEditorWebView()) {
                    return false;
                }
                var widgets = getWidgets();
                // cannot save if widget motion hasn't stopped
                return !lodash_1.default.some(widgets, 'shadow');
            }
            function isEditorWebView() {
                return navigationService.isEditor && !navigationService.isExport;
            }
            function isPageBreak(widget) {
                return widget && widget._type === PAGE_BREAK_WIDGET_TYPE;
            }
            return {
                getWidgets: getWidgets,
                addWidget: addWidget,
                deleteWidget: deleteWidget,
                saveWidget: saveWidget,
                finishTransform: finishTransform
            };
        }
    ]);
})(angular_1.module('qualtrics.pr'));
